import { useCallback, useEffect, useMemo, useReducer } from 'react'
import { useDispatch } from 'react-redux'
import _ from 'lodash'
import { useCanEdit, useIntersectionObserver } from '~/hooks'
import { useRouteContext } from '~/routes'
import { bshSlice } from '~/store'
import { compareObjects } from '~/utils'

const alwaysEditable = false

const actions = {
  EDITABLE: 'EDITABLE',
  PARAGRAPH: 'PARAGRAPH',
  PROPERTY: 'PROPERTY',
  SAVEABLE: 'SAVEABLE',
}

const getPropertyValue = (object, property) => _.get(object, property)

const getRootProperty = (property) => property.split('.')[0]

export const getSaveData = (property, paragraph) => {
  if (!property.includes('.')) {
    return { [property]: paragraph[property] }
  }

  const root = getRootProperty(property)
  return { [root]: paragraph[root] }
}

const isSaveable = (original, paragraph, property, value) => {
  if (property?.includes('.')) {
    const next =
      value !== undefined ? value : getPropertyValue(paragraph, property)
    return !compareObjects(getPropertyValue(original, property), next)
  }

  const saveableProps = [
    'name',
    'title',
    'title_align',
    'title_valign',
    'icon_caption',
    'text',
    'euro_title',
    'euro_text',
    'mex_title',
    'mex_text',
    'shops',
  ]
  return saveableProps.reduce((acc, p) => {
    if (acc) return acc
    if (p === property) {
      return !compareObjects(original[p], value)
    }
    return !compareObjects(original[p], paragraph[p])
  }, false)
}

const reducer = (state, action) => {
  switch (action.type) {
    case actions.EDITABLE:
      return {
        ...state,
        editable: action.value,
      }
    case actions.PARAGRAPH:
      return {
        ...state,
        paragraph: action.value,
        original: action.value,
        saveable: false,
      }
    case actions.PROPERTY: {
      const paragraph = { ...state.paragraph, [action.property]: action.value }
      return {
        ...state,
        paragraph,
        saveable: isSaveable(
          state.original,
          state.paragraph,
          action.property,
          action.value
        ),
      }
    }
    case actions.SAVEABLE:
      return {
        ...state,
        saveable: action.value && isSaveable(state.original, state.paragraph),
      }
    default:
      return { ...state }
  }
}

const useParagraph = (props) => {
  const { paragraph: paragraphProp, ref, onEditing, ...rest } = props
  const dispatchRedux = useDispatch()
  const canEdit = useCanEdit()
  const { product } = useRouteContext('useParagraph')

  const reducerBag = useReducer(reducer, {
    editable: false,
    paragraph: paragraphProp,
    original: paragraphProp,
    saveable: false,
  })
  const [{ editable, original, paragraph, saveable }, dispatch] = reducerBag
  useEffect(() => {
    dispatch({ type: actions.PARAGRAPH, value: paragraphProp })
  }, [dispatch, paragraphProp])

  const onPropertyChange = useCallback(
    (property, value) => {
      dispatch({ type: actions.PROPERTY, property, value })
    },
    [dispatch]
  )

  const resetProperty = useCallback(
    (property) => {
      const value = getPropertyValue(original, property)
      if (!property.includes('.')) {
        dispatch({ type: actions.PROPERTY, property, value })
        return
      }

      const root = getRootProperty(property)
      const next = _.cloneDeep(paragraph[root]) ?? {}
      _.set(next, property.slice(root.length + 1), value)
      dispatch({ type: actions.PROPERTY, property: root, value: next })
    },
    [dispatch, original, paragraph]
  )

  const diffFromParent = useCallback(
    async (fields = undefined) => {
      return await dispatchRedux(
        bshSlice.diffParagraphFromParent({ data: { paragraph, fields } })
      ).unwrap()
    },
    [dispatchRedux, paragraph]
  )

  const restoreToParent = useCallback(
    async (fields = undefined) => {
      await dispatchRedux(
        bshSlice.restoreParagraphToParent({ data: { paragraph, fields } })
      )
    },
    [dispatchRedux, paragraph]
  )

  const restoreShopToParent = useCallback(
    async (shop) => {
      await dispatchRedux(
        bshSlice.restoreParagraphShopToParent({ data: { paragraph, shop } })
      )
    },
    [dispatchRedux, paragraph]
  )

  const saveParagraph = useCallback(
    async (data = undefined, overwrite = false) => {
      const saveable = data
        ? Object.keys(data).reduce((acc, field) => {
            return acc || isSaveable(original, paragraph, field, data[field])
          }, false)
        : false
      dispatch({ type: actions.SAVEABLE, value: saveable })
      await dispatchRedux(
        bshSlice.saveParagraph({
          data: {
            model: paragraph.model,
            id: paragraph.id,
            overwrite,
            product: !!product,
            ...(data ? { ...data } : { ...paragraph }),
          },
        })
      )
    },
    [dispatch, dispatchRedux, original, paragraph, product]
  )

  const saveAlt = useCallback(async () => {
    await dispatchRedux(bshSlice.saveParagraphAlt({ data: { paragraph } }))
  }, [dispatchRedux, paragraph])

  const saveCaption = useCallback(async () => {
    await dispatchRedux(bshSlice.saveParagraphCaption({ data: { paragraph } }))
  }, [dispatchRedux, paragraph])

  const removeParagraph = useCallback(async () => {
    await dispatchRedux(bshSlice.removeParagraph({ data: paragraph }))
    rest.onRemoved?.(paragraph)
  }, [dispatchRedux, paragraph, rest])

  const uploadImage = useCallback(
    async (type, file) => {
      await dispatchRedux(
        bshSlice.uploadParagraphImage({ data: { paragraph, file, type } })
      )
    },
    [dispatchRedux, paragraph]
  )

  const deleteImage = useCallback(
    async (type) => {
      await dispatchRedux(
        bshSlice.deleteParagraphImage({ data: { paragraph, type } })
      )
    },
    [dispatchRedux, paragraph]
  )

  const setEditable = useCallback(
    (value) => {
      setTimeout(() => {
        dispatch({ type: actions.EDITABLE, value })
      }, 0)
    },
    [dispatch]
  )

  const isDirty = useCallback(
    (property) => {
      return !compareObjects(
        getPropertyValue(original, property),
        getPropertyValue(paragraph, property)
      )
    },
    [original, paragraph]
  )

  const ratio = useIntersectionObserver({ ref })
  useEffect(() => {
    setEditable(ratio > 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratio])

  useEffect(() => {
    onEditing?.(paragraph.id, saveable)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveable])

  return useMemo(
    () => ({
      ...rest,
      canEdit,
      editable: canEdit && (alwaysEditable || editable),
      original,
      paragraph,
      saveable,
      onPropertyChange,
      diffFromParent,
      restoreToParent,
      restoreShopToParent,
      saveParagraph,
      removeParagraph,
      uploadImage,
      deleteImage,
      setEditable,
      saveAlt,
      saveCaption,
      isDirty,
      resetProperty,
    }),
    [
      rest,
      canEdit,
      editable,
      original,
      paragraph,
      saveable,
      onPropertyChange,
      diffFromParent,
      restoreToParent,
      restoreShopToParent,
      saveParagraph,
      removeParagraph,
      uploadImage,
      deleteImage,
      setEditable,
      saveAlt,
      saveCaption,
      isDirty,
      resetProperty,
    ]
  )
}

export default useParagraph
